import { FormControl, FormHelperText } from "@mui/material";
import _ from "lodash";
import { useEffect, useMemo, useState, type FC } from "react";
import {
  Controller,
  useFormContext,
  useWatch,
  type FieldValues,
  type UseFormSetValue,
} from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { isInnerItemEditingAtom, Log, type ICustomField } from "@ryde/shared";

import { GroupTitleTemplate } from "../group_title.template";
import { AvailabilityItemAdd } from "./availability-item.add";
import { AvailabilityRow } from "./availability-row";
import {
  availabilitySerialized,
  type IAvailabilityItem,
} from "./traffic-availability.models";
import { TrafficAvailabilityStyled } from "./traffic-availability.styled";

const normalizeAvailabilityListFieldData = (
  watchedList: unknown,
  fieldName: string,
  setValue: UseFormSetValue<FieldValues>,
): IAvailabilityItem[] => {
  if (watchedList && !_.isArray(watchedList)) {
    /* guard for invalid value coming from backend*/
    setValue(fieldName, []);
    return [];
  }

  return watchedList as IAvailabilityItem[];
};

export const TrafficAvailabilityTemplate: FC<ICustomField> = ({
  name,
  formAttributes,
}) => {
  const { control, setValue } = useFormContext();
  const watchedValue = useWatch({ control, name });
  const [editingCount, setEditingCount] = useState(0);
  const setIsInnerItemEditing = useSetRecoilState(isInnerItemEditingAtom);

  const availabilityList = useMemo(() => {
    const availabilityListValue = normalizeAvailabilityListFieldData(
      watchedValue,
      name,
      setValue,
    );
    Log.debug("Contacts list normalized value", availabilityListValue);
    return availabilityListValue;
  }, [name, setValue, watchedValue]);

  const removeHandler = useEvent((index: number) => {
    Log.debug("Remove availability handler fired for index ", index);
    availabilityList?.splice(index, 1);
    setValue(name, availabilityList || [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  });

  const { disabled } = formAttributes || {};

  const handleEditStart = useEvent(() => {
    setEditingCount(prev => prev + 1);
  });

  const handleEditEnd = useEvent(() => {
    setEditingCount(prev => Math.max(0, prev - 1));
  });

  useEffect(() => {
    setIsInnerItemEditing(editingCount > 0);
  }, [editingCount, setIsInnerItemEditing]);

  useEffect(() => {
    return () => {
      setIsInnerItemEditing(false);
    };
  }, [setIsInnerItemEditing]);

  return (
    <TrafficAvailabilityStyled>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => {
          return (
            <FormControl
              error={!!error}
              data-e2e-template="traffic-availability-template"
            >
              <GroupTitleTemplate
                formLabel="trafficAvailability"
                name={name}
                formAttributes={formAttributes}
              />

              <div
                className="availability__container"
                data-inner-editing={editingCount > 0 ? "true" : undefined}
              >
                {availabilityList?.map((availabilityField, index) => {
                  return (
                    <AvailabilityRow
                      key={`${index}_${availabilitySerialized(availabilityField)}`}
                      fieldName={name}
                      index={index}
                      removeHandler={removeHandler}
                      parentControl={control}
                      parentSetValue={setValue}
                      disabledEdit={disabled}
                      disabledRemove={disabled}
                      onEditStart={handleEditStart}
                      onEditEnd={handleEditEnd}
                    />
                  );
                })}
                {!disabled && (
                  <AvailabilityItemAdd
                    fieldName={name}
                    index={availabilityList?.length || 0}
                    setValueOuter={setValue}
                    formAttributes={formAttributes}
                    onEditStart={handleEditStart}
                    onEditEnd={handleEditEnd}
                  />
                )}
              </div>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          );
        }}
      />
    </TrafficAvailabilityStyled>
  );
};
